import { useEffect, useState } from "react";
import { http, compact, currency } from "@/lib/api";
import { Users, Sparkles, Eye, Package } from "lucide-react";

// Threshold below which we hide the "Monthly visitors" card entirely — real
// traffic count comes from hover_events over the trailing 30 days, so early
// launch numbers would look unfairly small next to the other stats.
const VISITORS_MIN_TO_DISPLAY = 5000;

const ALL_ITEMS = [
  { key: "units_sold", label: "Units claimed", icon: Package, fmt: compact, tint: "from-violet-500 to-fuchsia-500" },
  { key: "businesses_listed", label: "Businesses", icon: Users, fmt: compact, tint: "from-cyan-500 to-teal-500" },
  { key: "monthly_visitors", label: "Monthly visitors", icon: Eye, fmt: compact, tint: "from-fuchsia-500 to-pink-500" },
  { key: "units_available", label: "Units available", icon: Sparkles, fmt: compact, tint: "from-amber-400 to-orange-500" },
];

export default function StatsBar() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => http.get("/stats").then(({ data }) => alive && setS(data)).catch(() => {});
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Only include the visitors card once real traffic hits the threshold. Until
  // then we render a clean 3-card grid instead of a hollow "1.0k" number.
  const items = ALL_ITEMS.filter((it) => {
    if (it.key !== "monthly_visitors") return true;
    return s && (s.monthly_visitors ?? 0) >= VISITORS_MIN_TO_DISPLAY;
  });

  const gridCols = items.length === 3 ? "md:grid-cols-3" : "md:grid-cols-4";

  return (
    <section data-testid="stats-bar" className={`max-w-6xl mx-auto px-4 grid grid-cols-2 ${gridCols} gap-3`}>
      {items.map(({ key, label, icon: Icon, fmt, tint }, i) => (
        <div
          key={key}
          data-testid={`stat-${key}`}
          className="glass p-5 animate-fade-up"
          style={{ animationDelay: `${i * 80}ms` }}
        >
          <div className="flex items-center gap-2 text-[var(--fg-muted)] text-xs uppercase tracking-widest">
            <span className={`w-1.5 h-6 rounded-full bg-gradient-to-b ${tint}`} />
            {label}
          </div>
          <div className="mt-2 text-3xl font-display font-medium leading-none">
            {s ? fmt(s[key]) : "—"}
          </div>
          {key === "units_sold" && s && (
            <div className="mt-1 text-xs font-mono text-[var(--fg-muted)]">
              {currency(s.revenue)} generated
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
